import { Link } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as React from 'react';
import { SubmitHandler } from 'react-hook-form';
import { Link as LinkRouter } from 'react-router-dom';
import UserContext from '../../../context/userContext';
import GridWithToolbar from '../../grid/GridWithToolbar';
import { successToast } from '../../Toast';
import { UsersApi } from './api';
import { UsersForm } from './components';
import { UserInfo, UserRequest } from './types';

export default function UsersGrid() {
	const queryClient = useQueryClient();
	const { selectedUserRole: user } = React.useContext(UserContext);
	const [isAddUserConfirmationModalOpen, setIsAddUserConfirmationModalOpen] =
		React.useState(false);
	const USER_KEY = UsersApi.USERS_KEY + '-' + user.namespace;

	const addUserMigration = useMutation(UsersApi.addUser, {
		onSuccess: (data) => {
			// Add new user to the list
			const currUsers = queryClient.getQueryData<UserInfo[]>([USER_KEY]);
			queryClient.setQueryData([USER_KEY], [...currUsers, data]);

			queryClient.invalidateQueries([USER_KEY]);
			queryClient.setQueryData([USER_KEY, data.userUuid], () => data);

			successToast('User added');
			setIsAddUserConfirmationModalOpen(false);
		},
	});

	const handleAddUser: SubmitHandler<UserRequest> = (values) => {
		addUserMigration.mutate(values);
	};

	const { isLoading, isRefetching, data } = useQuery({
		queryKey: [USER_KEY],
		queryFn: UsersApi.getUsers,
	});

	const ActiveRolesRow = (params: GridRenderCellParams) => (
		<Link
			underline='hover'
			component={LinkRouter}
			to={`/admin/users/${params.row.userUuid}`}
		>
			{params.value}
		</Link>
	);

	const columns: GridColDef[] = [
		{
			field: 'username',
			headerName: 'Username',
			minWidth: 200,
			renderCell: ActiveRolesRow,
		},
		{
			field: 'firstName',
			headerName: 'First name',
			minWidth: 200,
		},
		{
			field: 'lastName',
			headerName: 'Last name',
			minWidth: 200,
		},
		{
			field: 'email',
			headerName: 'Email',
			flex: 1,
		},
	];

	return (
		<>
			<GridWithToolbar
				title='Users'
				key={data?.length}
				isRefetching={isRefetching}
				data={data}
				rowId='userUuid'
				columns={columns}
				loading={isLoading}
				buttonText='Add User'
				onButtonClick={() => setIsAddUserConfirmationModalOpen(true)}
			/>
			<Dialog
				open={isAddUserConfirmationModalOpen}
				onClose={() => setIsAddUserConfirmationModalOpen(false)}
			>
				<DialogTitle>Add User</DialogTitle>
				<DialogContent sx={{ width: 600 }}>
					<UsersForm
						initialValues={{
							firstName: '',
							lastName: '',
							email: '',
							username: '',
						}}
						isLoading={addUserMigration.isLoading}
						handleSubmit={handleAddUser}
						handleClose={() => setIsAddUserConfirmationModalOpen(false)}
					/>
				</DialogContent>
			</Dialog>
		</>
	);
}
